import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../api';
import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Link,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import Breadcrumb from '../components/Breadcrumb';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { formatDate } from '../utils/dateUtils';
import { useToast } from '../components/ToastProvider';
import { generateQuotationPdf } from '../utils/generatePdf';
import PageLoader from '../components/ui/PageLoader';
import PageHeader from '../components/ui/PageHeader';
import { pageContainerSx } from '../styles/tokens';

const labelSx = {
  fontSize: '0.8125rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'text.secondary',
  mb: 0.5,
  display: 'block',
};

const QuotationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const showToast = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);

  const fetchDetails = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await axios.get(`/api/quotations/${id}`);
      setDetails(res.data);
    } catch (error) {
      showToast('Error loading quotation details', 'error');
      setDetails(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [id]);

  const openQuotationPdf = async () => {
    if (!details?.id) return;
    setPdfLoading(true);
    try {
      await generateQuotationPdf(details.id);
      showToast('PDF generated successfully', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to generate PDF', 'error');
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return <PageLoader message="Loading quotation details..." />;
  }

  if (!details) {
    return (
      <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto', p: 3 }}>
        <Typography color="text.secondary">Quotation not found.</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/quotations')} sx={{ mt: 2, textTransform: 'none' }}>
          Back to Quotations
        </Button>
      </Box>
    );
  }

  const products = details.products || [];

  return (
    <Box sx={{ ...pageContainerSx, maxWidth: 720, px: { xs: 1.5, sm: 2 }, pb: 4 }}>
      <Breadcrumb items={[{ label: 'Quotations', path: '/quotations' }, { label: `Quotation #${details.id}`, path: null }]} />
      <PageHeader
        title={`Quotation #${details.id}`}
        subtitle={details.is_converted ? 'Converted to booking' : 'Not converted'}
        actions={(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {!details.is_converted && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                onClick={() => navigate(`/quotations?edit=${details.id}`)}
                sx={{ textTransform: 'none', fontSize: '0.8125rem', borderRadius: 999, height: 40, px: 2 }}
              >
                Edit
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={pdfLoading ? <CircularProgress size={14} color="inherit" /> : <PictureAsPdfIcon sx={{ fontSize: 14 }} />}
              onClick={openQuotationPdf}
              disabled={pdfLoading}
              sx={{ textTransform: 'none', fontSize: '0.8125rem', borderRadius: 999, height: 40, px: 2.5 }}
            >
              Export PDF
            </Button>
          </Box>
        )}
      />
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >

        <Box sx={{ p: { xs: 2, sm: 3 }, '& > * + *': { mt: { xs: 2.5, sm: 3 } } }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: { xs: 2, sm: 3 } }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={labelSx}>Customer</Typography>
              <Typography sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1rem' }}>
                {details.customer ?? '—'}
              </Typography>
              {(details.company_name != null && details.company_name !== '') && (
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  {details.company_name}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 }}>
              <Box>
                <Typography sx={labelSx}>Quotation Date</Typography>
                <Typography sx={{ fontWeight: 500, fontSize: '0.75rem', color: 'text.primary' }}>
                  {formatDate(details.quotation_date) || '—'}
                </Typography>
              </Box>
              <Box>
                <Typography sx={labelSx}>Event Date</Typography>
                <Typography sx={{ fontWeight: 500, fontSize: '0.75rem', color: 'text.primary' }}>
                  {formatDate(details.event_date) || '—'}
                </Typography>
              </Box>
              <Box>
                <Typography sx={labelSx}>Return Date</Typography>
                <Typography sx={{ fontWeight: 500, fontSize: '0.75rem', color: 'text.primary' }}>
                  {formatDate(details.return_date) || '—'}
                </Typography>
              </Box>
            </Box>
          </Box>

          {details.is_converted && details.booking_id && (
            <Box>
              <Typography sx={labelSx}>Booking</Typography>
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate('/bookings')}
                sx={{ fontWeight: 600 }}
              >
                View Booking #{details.booking_id}
              </Link>
            </Box>
          )}

          <Box>
            <Typography sx={{ ...labelSx, mb: 1 }}>Products</Typography>
            {isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {products.map((p, i) => (
                  <Paper
                    key={i}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 1.25,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '1rem', fontWeight: 500, color: 'text.primary' }}>
                        {i + 1}. {p.product_name ?? '—'}
                      </Typography>
                      <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.primary' }}>
                        ₹{typeof p.price === 'number' && typeof p.quantity === 'number' ? (p.price * p.quantity).toFixed(2) : '0'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mt: 0.5, fontSize: '0.875rem', color: 'text.secondary' }}>
                      <span>Qty: {p.quantity}</span>
                      <span>Rate: ₹{typeof p.price === 'number' ? p.price.toFixed(2) : p.price ?? '0'}</span>
                    </Box>
                  </Paper>
                ))}
              </Box>
            ) : (
              <TableContainer sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 360 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, width: 40, fontSize: '0.8125rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>
                        #
                      </TableCell>
                      <TableCell sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, minWidth: 120, fontSize: '0.8125rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>
                        Product
                      </TableCell>
                      <TableCell align="right" sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, width: 48, fontSize: '0.8125rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>
                        Qty
                      </TableCell>
                      <TableCell align="right" sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, width: 80, fontSize: '0.8125rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>
                        Rate
                      </TableCell>
                      <TableCell align="right" sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, width: 80, fontSize: '0.8125rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>
                        Amount
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.map((p, i) => (
                      <TableRow key={i} sx={{ '&:last-child td': { borderBottom: 0 }, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <TableCell sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, fontSize: '1rem', color: 'text.secondary' }}>
                          {i + 1}
                        </TableCell>
                        <TableCell sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, minWidth: 120, fontSize: '1rem', color: 'text.primary' }}>
                          {p.product_name ?? '—'}
                        </TableCell>
                        <TableCell align="right" sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, fontSize: '1rem', color: 'text.primary' }}>
                          {p.quantity}
                        </TableCell>
                        <TableCell align="right" sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, fontSize: '1rem', color: 'text.secondary' }}>
                          ₹{typeof p.price === 'number' ? p.price.toFixed(2) : p.price ?? '0'}
                        </TableCell>
                        <TableCell align="right" sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, fontSize: '1rem', color: 'text.primary' }}>
                          ₹{typeof p.price === 'number' && typeof p.quantity === 'number' ? (p.price * p.quantity).toFixed(2) : '0'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
            <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.primary' }}>
              Total: ₹{details.total_amount != null ? Number(details.total_amount).toFixed(2) : '0'}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/quotations')}
        sx={{ mt: 2, textTransform: 'none' }}
      >
        Back to Quotations
      </Button>
    </Box>
  );
};

export default QuotationDetails;
